/**
 * AdminWakePhrasesPage.jsx — admin CRUD for Hustle Voice wake phrases.
 *
 * Only accessible to admin users (backend enforces require_admin).
 * Lets ops change the default phrases that ship to every user
 * (per-user overrides live on /account/voice).
 */
import { useEffect, useState } from "react";
import { API_BASE } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  slug: "",
  module: "",
  phrase: "",
  action: "open_module",
  deep_link: "",
  description: "",
  enabled: true,
};

export default function AdminWakePhrasesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // slug being edited, or "new"
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/voice/wake-phrases/admin`, { credentials: "include" });
      const j = await r.json();
      setItems(j.items || []);
    } catch (e) {
      toast.error("Could not load admin wake-phrase list — are you signed in as admin?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (item) => {
    setEditing(item.slug);
    setDraft({ ...item });
    setValidation(null);
  };

  const startNew = () => {
    setEditing("new");
    setDraft(EMPTY);
    setValidation(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/voice/wake-phrases/admin`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const j = await r.json();
      if (!r.ok) {
        const reasons = j?.detail?.reasons || [j?.detail || "Save failed"];
        setValidation({ ok: false, reasons });
        toast.error(reasons[0] || "Save failed");
        return;
      }
      toast.success("Saved");
      setEditing(null);
      setDraft(EMPTY);
      setValidation(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete wake phrase "${slug}"?`)) return;
    const r = await fetch(`${API_BASE}/voice/wake-phrases/admin/${slug}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      toast.error(j?.detail || "Delete failed");
      return;
    }
    toast.success("Deleted");
    await load();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 px-4 py-8" data-testid="admin-wake-phrases">
      <div className="max-w-5xl mx-auto">
        <header className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Wake phrases — admin</h1>
            <p className="text-slate-400 text-sm mt-1">
              Default voice triggers across the Hustle Suite. Users can override these for their own account.
            </p>
          </div>
          <button
            data-testid="add-wake-phrase"
            onClick={startNew}
            className="bg-amber-400 text-slate-900 font-semibold px-4 py-2 rounded text-sm">
            + Add phrase
          </button>
        </header>

        {loading && <p className="text-slate-400">Loading…</p>}

        {!loading && (
          <div className="overflow-x-auto border border-slate-800 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Slug</th>
                  <th className="text-left px-3 py-2">Module</th>
                  <th className="text-left px-3 py-2">Phrase</th>
                  <th className="text-left px-3 py-2">Action / Deep link</th>
                  <th className="text-left px-3 py-2">Enabled</th>
                  <th className="text-right px-3 py-2">—</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.slug} className="border-t border-slate-800">
                    <td className="px-3 py-2 font-mono text-xs">{it.slug}</td>
                    <td className="px-3 py-2">{it.module}</td>
                    <td className="px-3 py-2 font-semibold">{it.phrase}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="px-1.5 py-0.5 rounded bg-slate-800 mr-2">{it.action}</span>
                      <span className="text-slate-500 font-mono">{it.deep_link}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${it.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>
                        {it.enabled ? "on" : "off"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => startEdit(it)} className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 mr-1" data-testid={`edit-admin-${it.slug}`}>Edit</button>
                      {!["master", "emergency"].includes(it.slug) && (
                        <button onClick={() => remove(it.slug)} className="text-xs px-2 py-1 rounded bg-rose-900/40 hover:bg-rose-900/60 text-rose-300" data-testid={`delete-admin-${it.slug}`}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-4">
                {editing === "new" ? "New wake phrase" : `Edit "${draft.slug}"`}
              </h2>
              <div className="space-y-3">
                {editing === "new" && (
                  <label className="block">
                    <span className="text-xs uppercase text-slate-400">Slug</span>
                    <input
                      value={draft.slug}
                      onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                      className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base font-mono"
                      placeholder="e.g. lost-pet"
                    />
                  </label>
                )}
                <label className="block">
                  <span className="text-xs uppercase text-slate-400">Module</span>
                  <input
                    value={draft.module}
                    onChange={(e) => setDraft({ ...draft, module: e.target.value })}
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base"
                    placeholder="e.g. tackle, plaza, directory"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase text-slate-400">Phrase</span>
                  <input
                    value={draft.phrase}
                    onChange={(e) => setDraft({ ...draft, phrase: e.target.value })}
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base"
                    placeholder="e.g. Hustle Pet Lost"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase text-slate-400">Action</span>
                  <select
                    value={draft.action}
                    onChange={(e) => setDraft({ ...draft, action: e.target.value })}
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base">
                    <option value="open_app">open_app</option>
                    <option value="open_module">open_module</option>
                    <option value="open_screen">open_screen</option>
                    <option value="open_scanner">open_scanner</option>
                    <option value="search_module">search_module</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs uppercase text-slate-400">Deep link</span>
                  <input
                    value={draft.deep_link}
                    onChange={(e) => setDraft({ ...draft, deep_link: e.target.value })}
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base font-mono"
                    placeholder="hustleapp://module/path"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase text-slate-400">Description</span>
                  <textarea
                    rows={2}
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base"
                    placeholder="What this phrase does"
                  />
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!draft.enabled}
                    onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                  />
                  <span className="text-sm">Enabled</span>
                </label>
              </div>

              {validation && !validation.ok && (
                <div className="mt-4 text-xs text-rose-400 border border-rose-900/50 rounded p-3 bg-rose-950/30">
                  <p className="font-semibold mb-1">AI validation failed:</p>
                  <ul className="list-disc list-inside">
                    {(validation.reasons || []).map((r, i) => <li key={i}>{typeof r === "string" ? r : JSON.stringify(r)}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => { setEditing(null); setValidation(null); }}
                  className="px-4 py-2 rounded text-sm text-slate-400">
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving || !draft.phrase || !draft.module || !draft.deep_link}
                  data-testid="save-admin-wake"
                  className="bg-amber-400 text-slate-900 font-semibold px-4 py-2 rounded text-sm disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
